/**
 * Session logs service — Firestore access for the `sessionLogs` collection.
 *
 * One document per class (doc id === classId), created automatically by the
 * `onClassCreatedSessionLog` Cloud Function and create-or-merged here as a
 * fallback. It is the durable record behind mentor attendance, the session-log
 * workflow, and the payout dashboard.
 *
 * A sessionLog document:
 *   {
 *     classId, mentorId, studentId,         // identity (denormalized for rules)
 *     title, subject,                       // display snapshot
 *     scheduledStartAt: Timestamp,          // = class.startAt snapshot
 *     durationMin, hourlyRate,              // payout inputs snapshot
 *     verificationDeadline: Timestamp,      // scheduledStartAt + 48h
 *     mentorJoinClickedAt, studentJoinClickedAt,  // mirrors of class join times
 *     // mentor-authored log:
 *     wasConducted, topicsCovered, homeworkAssigned, remarks, notesFileUrl,
 *     coveredModules,                       // course syllabus modules ticked off this session
 *     understandingRating, engagementRating, // 1..5 (0 = not rated) — powers progress trends
 *     mentorSubmittedLog, mentorSubmittedAt,
 *     // admin-controlled:
 *     statusOverride, paymentStatus,
 *     createdAt, updatedAt
 *   }
 *
 * Arrival status, session status and the payout amounts are NOT stored — they
 * are derived on read from the class + this doc (see features/payouts/rules).
 */
import firestoreService, {
  where,
  serverTimestamp,
  Timestamp,
  arrayUnion,
} from './firestore.service';

const SESSION_LOGS = 'sessionLogs';

const VERIFICATION_WINDOW_MS = 48 * 60 * 60 * 1000;

/** Clamp a star rating into the 0..5 range (0 = not rated). */
function clampRating(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.min(5, Math.max(0, Math.round(n)));
}

/** Normalize a covered-modules list: trimmed, non-empty, de-duplicated strings. */
function sanitizeModules(value) {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.map((m) => (m || '').trim()).filter(Boolean))];
}

/** Coerce a Timestamp | Date | ms | ISO into a Firestore Timestamp (or null). */
function toTimestamp(value) {
  if (!value) return null;
  if (value instanceof Timestamp) return value;
  if (typeof value?.toDate === 'function') return value; // already a Timestamp-like
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : Timestamp.fromDate(date);
}

/**
 * Build one immutable audit-trail entry. Stored values are never deleted — every
 * action appends a new entry. `serverTimestamp()` can't live inside an array, so
 * entries carry a client `Timestamp.now()` for `at`.
 *
 * @param {string} action   machine action key (see AUDIT_ACTION below)
 * @param {string} summary  human-readable one-liner for the timeline
 * @param {object} [actor]  { id, name, role }
 * @param {object} [extra]  { from, to, reason }
 */
function auditEntry(action, summary, actor = {}, extra = {}) {
  const entry = {
    at: Timestamp.now(),
    action,
    summary,
    actorId: actor.id || '',
    actorName: actor.name || '',
    actorRole: actor.role || '',
  };
  if (extra.from !== undefined) entry.from = extra.from ?? null;
  if (extra.to !== undefined) entry.to = extra.to ?? null;
  if (extra.reason) entry.reason = extra.reason;
  return entry;
}

export const AUDIT_ACTION = Object.freeze({
  CREATED: 'session_created',
  MENTOR_SUBMITTED: 'mentor_submitted_log',
  MENTOR_EDITED: 'mentor_edited_log',
  ADMIN_MODIFIED: 'admin_modified_session',
  STATUS_CHANGED: 'status_changed',
  PAYOUT_CHANGED: 'payout_changed',
  ATTENDANCE_OVERRIDDEN: 'attendance_overridden',
  PENALTY_APPLIED: 'penalty_applied',
  BONUS_ADDED: 'bonus_added',
  LOG_REOPENED: 'log_reopened',
  LOG_LOCKED: 'log_locked',
});

/**
 * Build the initial stored stub from a derived session-log row (the shape
 * produced by deriveSessionLog). Only used as a client-side fallback when the
 * Cloud Function hasn't created the doc yet.
 */
function stubFromRow(row) {
  const startTs = toTimestamp(row.scheduledAt);
  const deadlineTs =
    row.verificationDeadline != null
      ? Timestamp.fromMillis(row.verificationDeadline)
      : startTs
        ? Timestamp.fromMillis(startTs.toMillis() + VERIFICATION_WINDOW_MS)
        : null;
  return {
    classId: row.classId,
    mentorId: row.mentorId || '',
    studentId: row.studentId || '',
    // Copied from the class so progress rolls up to the right course by id.
    // Must stay in step with `buildSessionLogStub` in functions/src/payouts.js.
    enrollmentId: row.enrollmentId || '',
    title: row.title || '',
    subject: row.subject || '',
    scheduledStartAt: startTs,
    durationMin: row.durationMin || 60,
    // Deliberately null on a client-created stub, and required to be null by
    // the `sessionLogs` create rule. `deriveSessionLog` then prices the session
    // from the `mentors` record (which only an admin can write) rather than
    // from a number its own author supplied. The server's `buildSessionLogStub`
    // still snapshots the real rate — that path runs with the Admin SDK and is
    // how historical payouts stay stable when a rate later changes.
    hourlyRate: null,
    verificationDeadline: deadlineTs,
    // Attendance is read from the class, never from these mirrors (see
    // features/payouts/rules.js). Written null so a client-created stub cannot
    // assert a join that the class does not record.
    mentorJoinClickedAt: null,
    studentJoinClickedAt: null,
    wasConducted: null,
    topicsCovered: '',
    homeworkAssigned: '',
    studentPerformance: '',
    parentNotes: '',
    remarks: '',
    coveredModules: [],
    understandingRating: 0,
    engagementRating: 0,
    notesFileUrl: '',
    attachments: [],
    mentorSubmittedLog: false,
    mentorSubmittedAt: null,
    statusOverride: null,
    paymentStatus: 'pending',
    // admin adjustment surface
    attendanceOverride: null,
    adminBonus: 0,
    adminPenalty: 0,
    payoutOverride: null,
    adminModified: false,
    adminModifiedAt: null,
    adminModifiedBy: '',
    adminReopened: false,
    lockedAt: null,
    auditTrail: [auditEntry('session_created', 'Session created.', { role: 'system' })],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
}

export const sessionLogsService = {
  /** Realtime subscription to every session log (admin console). */
  subscribe(onData, onError) {
    return firestoreService.subscribeCollection(SESSION_LOGS, [], onData, onError);
  },

  /**
   * Realtime subscription to a single mentor's session logs. Filtered
   * server-side by `mentorId` (enforced again by Firestore rules); the caller
   * merges these with the mentor's classes, so no ordering/index is needed.
   */
  subscribeByMentor(mentorId, onData, onError) {
    return firestoreService.subscribeCollection(
      SESSION_LOGS,
      [where('mentorId', '==', mentorId)],
      onData,
      onError
    );
  },

  /**
   * Realtime subscription to a single student's session logs. Students see the
   * details of every session booked for them (rules scope this to their own
   * `studentId`); the UI surfaces submitted logs as read-only history.
   */
  subscribeByStudent(studentId, onData, onError) {
    return firestoreService.subscribeCollection(
      SESSION_LOGS,
      [where('studentId', '==', studentId)],
      onData,
      onError
    );
  },

  get(classId) {
    return firestoreService.getDocument(SESSION_LOGS, classId);
  },

  /**
   * Ensure a stored stub exists for a class. Idempotent: reads first and only
   * creates when missing. Returns the (existing or new) document id.
   */
  async ensureForClass(row) {
    if (!row?.classId) return null;
    const existing = await firestoreService.getDocument(SESSION_LOGS, row.classId);
    if (existing) return existing;
    await firestoreService.setDocument(SESSION_LOGS, row.classId, stubFromRow(row));
    return { id: row.classId };
  },

  /**
   * Persist a mentor's session log. Ensures the stub exists, then writes only
   * the mentor-authored fields (Firestore rules restrict mentors to exactly
   * these keys). The submission flips `mentorSubmittedLog` true, appends an
   * audit entry, and the payout is recomputed on read.
   *
   * Rejects if the log is locked (its 48-hour window elapsed and an admin has
   * not reopened it) — mentors cannot edit locked logs. Admins (actor.role ===
   * 'admin') bypass the lock and may author/correct the log on the mentor's
   * behalf; the edit is stamped `adminModified` and audited under their name.
   *
   * @param {object} row    the derived session-log row being completed
   * @param {object} fields wasConducted, topicsCovered, homeworkAssigned,
   *                        studentPerformance, parentNotes, remarks,
   *                        coveredModules[], understandingRating, engagementRating,
   *                        notesFileUrl, attachments[]
   * @param {object} [actor] { id, name, role } for the audit trail
   */
  async submitLog(row, fields = {}, actor = {}) {
    const who = { role: 'mentor', ...actor };
    const isAdminEdit = who.role === 'admin';
    if (row.isLocked && !isAdminEdit) {
      throw new Error('This session log is locked. Ask an admin to reopen it.');
    }
    await this.ensureForClass(row);
    const editing = Boolean(row.submitted);
    const audit = isAdminEdit
      ? auditEntry(AUDIT_ACTION.ADMIN_MODIFIED, 'Admin edited the session log.', who)
      : editing
        ? auditEntry(AUDIT_ACTION.MENTOR_EDITED, 'Mentor edited the session log.', who)
        : auditEntry(AUDIT_ACTION.MENTOR_SUBMITTED, 'Mentor submitted the session log.', who);
    return firestoreService.updateDocument(SESSION_LOGS, row.classId, {
      ...(isAdminEdit ? { adminModified: true } : {}),
      wasConducted: fields.wasConducted ?? null,
      topicsCovered: (fields.topicsCovered || '').trim(),
      homeworkAssigned: (fields.homeworkAssigned || '').trim(),
      studentPerformance: (fields.studentPerformance || '').trim(),
      parentNotes: (fields.parentNotes || '').trim(),
      remarks: (fields.remarks || '').trim(),
      coveredModules: sanitizeModules(fields.coveredModules),
      understandingRating: clampRating(fields.understandingRating),
      engagementRating: clampRating(fields.engagementRating),
      notesFileUrl: fields.notesFileUrl || '',
      attachments: Array.isArray(fields.attachments) ? fields.attachments : row.attachments || [],
      mentorSubmittedLog: true,
      mentorSubmittedAt: serverTimestamp(),
      auditTrail: arrayUnion(audit),
    });
  },

  /**
   * Admin edit of the administrative surface. Ensures the doc exists first so an
   * admin can act on a class whose log hasn't been created yet, then writes only
   * the changed fields and appends one audit entry per change (previous values
   * are never destroyed). Stamps `adminModified` so every surface can flag it.
   *
   * `patch` may include: statusOverride, paymentStatus, attendanceOverride,
   * adminBonus, adminPenalty, payoutOverride, adminReopened. `reason` (optional)
   * is recorded on each audit entry.
   *
   * @param {object} row    the derived row (carries current values for the diff)
   * @param {object} patch
   * @param {object} [actor] { id, name, role }
   */
  async adminUpdate(row, patch = {}, actor = {}) {
    await this.ensureForClass(row);
    const who = { role: 'admin', ...actor };
    const reason = patch.reason || '';
    const data = {};
    const entries = [];

    if ('statusOverride' in patch && (patch.statusOverride || null) !== (row.statusOverride || null)) {
      data.statusOverride = patch.statusOverride || null;
      entries.push(
        auditEntry(AUDIT_ACTION.STATUS_CHANGED, 'Admin changed the session status.', who, {
          from: row.sessionStatus,
          to: patch.statusOverride || 'automatic',
          reason,
        })
      );
    }

    if ('attendanceOverride' in patch && (patch.attendanceOverride || null) !== (row.attendanceOverride || null)) {
      data.attendanceOverride = patch.attendanceOverride || null;
      entries.push(
        auditEntry(AUDIT_ACTION.ATTENDANCE_OVERRIDDEN, 'Admin overrode the attendance status.', who, {
          from: row.arrivalStatus,
          to: patch.attendanceOverride || 'automatic',
          reason,
        })
      );
    }

    if ('adminBonus' in patch && Number(patch.adminBonus || 0) !== Number(row.adminBonus || 0)) {
      data.adminBonus = Number(patch.adminBonus) || 0;
      entries.push(
        auditEntry(AUDIT_ACTION.BONUS_ADDED, 'Admin adjusted the bonus.', who, {
          from: row.adminBonus || 0,
          to: data.adminBonus,
          reason,
        })
      );
    }

    if ('adminPenalty' in patch && Number(patch.adminPenalty || 0) !== Number(row.adminPenalty || 0)) {
      data.adminPenalty = Number(patch.adminPenalty) || 0;
      entries.push(
        auditEntry(AUDIT_ACTION.PENALTY_APPLIED, 'Admin adjusted the penalty.', who, {
          from: row.adminPenalty || 0,
          to: data.adminPenalty,
          reason,
        })
      );
    }

    if ('payoutOverride' in patch) {
      const next = patch.payoutOverride === '' || patch.payoutOverride == null ? null : Number(patch.payoutOverride);
      if (next !== (row.payoutOverride ?? null)) {
        data.payoutOverride = next;
        entries.push(
          auditEntry(AUDIT_ACTION.PAYOUT_CHANGED, 'Admin set a manual payout amount.', who, {
            from: row.payoutOverride ?? row.earned,
            to: next,
            reason,
          })
        );
      }
    }

    if ('paymentStatus' in patch && patch.paymentStatus !== row.paymentStatus) {
      data.paymentStatus = patch.paymentStatus;
      entries.push(
        auditEntry(AUDIT_ACTION.PAYOUT_CHANGED, 'Admin changed the payout status.', who, {
          from: row.paymentStatus,
          to: patch.paymentStatus,
          reason,
        })
      );
    }

    if ('adminReopened' in patch && Boolean(patch.adminReopened) !== Boolean(row.adminReopened)) {
      data.adminReopened = Boolean(patch.adminReopened);
      entries.push(
        auditEntry(
          patch.adminReopened ? AUDIT_ACTION.LOG_REOPENED : AUDIT_ACTION.LOG_LOCKED,
          patch.adminReopened ? 'Admin reopened the locked log.' : 'Admin re-locked the log.',
          who,
          { reason }
        )
      );
    }

    if (entries.length === 0) return null; // nothing changed

    data.adminModified = true;
    data.adminModifiedAt = serverTimestamp();
    data.adminModifiedBy = who.name || who.id || 'admin';
    data.auditTrail = arrayUnion(...entries);

    return firestoreService.updateDocument(SESSION_LOGS, row.classId, data);
  },

  /** Admin shortcut: reopen a locked log so the mentor can edit it again. */
  reopen(row, actor = {}) {
    return this.adminUpdate(row, { adminReopened: true }, actor);
  },

  remove(classId) {
    return firestoreService.deleteDocument(SESSION_LOGS, classId);
  },
};

export default sessionLogsService;
